import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { ApiError, api } from '../lib/api';
import { CodeField } from './CodeField';
import { Button, Card, CardBody, ErrorNote, Field, inputClass } from './ui';

/**
 * Receive stock into a lot.
 *
 * The lot code is suggested by the API (item + origin + date + sequence) and
 * can be overridden with a supplier's own batch number — clearing the field
 * accepts the suggestion, and the server generates it either way rather than
 * trusting the browser to have the right idea.
 */
export function ReceiveStockForm({
  onDone,
  defaultItemId,
}: {
  onDone: () => void;
  /** Preselect a material — the dashboard's "receive this low item" shortcut. */
  defaultItemId?: string;
}) {
  const queryClient = useQueryClient();

  const [itemId, setItemId] = useState(defaultItemId ?? '');
  const [locationId, setLocationId] = useState('');
  const [uom, setUom] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [lotCode, setLotCode] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [reason, setReason] = useState('');

  const items = useQuery({
    queryKey: ['items', { limit: 200 }],
    queryFn: () => api.items.list({ limit: 200 }),
  });
  const locations = useQuery({
    queryKey: ['locations', { limit: 200 }],
    queryFn: () => api.locations.list({ limit: 200 }),
  });
  const suppliers = useQuery({
    queryKey: ['suppliers', { limit: 200 }],
    queryFn: () => api.suppliers.list({ limit: 200 }),
  });

  const selectedItem = items.data?.items.find((item) => item.id === itemId);
  const selectedLocation = locations.data?.items.find((location) => location.id === locationId);
  const units = selectedItem?.units?.map((unit) => unit.uom) ?? [];
  const effectiveUom = uom || selectedItem?.baseUom || '';

  const receive = useMutation({
    mutationFn: () =>
      api.inventory.receive({
        itemId,
        locationId,
        quantity: quantity.trim(),
        uom: effectiveUom,
        unitCost: unitCost.trim(),
        ...(lotCode.trim() ? { lotCode: lotCode.trim() } : {}),
        ...(supplierId ? { supplierId } : {}),
        ...(expiryDate
          ? { expiryDate: new Date(`${expiryDate}T00:00:00Z`).toISOString() }
          : {}),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
      void queryClient.invalidateQueries({ queryKey: ['lot-suggest'] });
      onDone();
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    receive.mutate();
  }

  const error = receive.error;

  return (
    <Card className="mb-6">
      <CardBody>
        <h2 className="text-base font-bold text-ink">Receive stock</h2>
        <p className="mt-1 text-sm font-light text-ink-body">
          Creates a lot and posts its first ledger entry. Cost lives on the lot, not the item.
        </p>

        <form
          autoComplete="off"
          onSubmit={handleSubmit}
          className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          <Field label="Item">
            <select
              className={inputClass}
              value={itemId}
              onChange={(event) => {
                setItemId(event.target.value);
                setUom('');
                setLotCode('');
              }}
              required
            >
              <option value="">Select an item…</option>
              {items.data?.items.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.code} — {item.name}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="Location"
            hint={selectedLocation?.requiresQcRelease ? 'QC hold: this stock will be quarantined' : undefined}
            tooltip="Where the material physically goes. If that location requires QC release, the new lot is created on hold — counted and valued, but unusable until a quality check passes."
          >
            <select
              className={inputClass}
              value={locationId}
              onChange={(event) => setLocationId(event.target.value)}
              required
            >
              <option value="">Select a location…</option>
              {locations.data?.items.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.code} — {location.name}
                </option>
              ))}
            </select>
          </Field>

          <CodeField
            key={itemId}
            label="Lot code"
            value={lotCode}
            onChange={setLotCode}
            queryKey={['lot-suggest', itemId]}
            queryFn={itemId ? () => api.inventory.suggestLotCode(itemId) : undefined}
            required={false}
            tooltip="A lot is one delivery or one production run of the item, with its own expiry and cost. The suggested code combines the item, how the lot came to be (PUR for a receipt), the date and a sequence number. Replace it with the supplier's own batch number if you need to match their paperwork; clear it to accept the suggestion."
            error={
              receive.error instanceof ApiError && receive.error.status === 409
                ? receive.error.message
                : undefined
            }
          />

          <Field label="Quantity">
            <div className="flex gap-2">
              <input
                name="quantity"
                className={inputClass}
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
                placeholder="100"
                inputMode="decimal"
                required
              />
              <select
                className={`${inputClass} max-w-[7rem]`}
                value={effectiveUom}
                onChange={(event) => setUom(event.target.value)}
                disabled={units.length === 0}
                name="uom"
                aria-label="Unit of measure"
              >
                {units.length === 0 ? <option value="">—</option> : null}
                {units.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
            </div>
          </Field>

          <Field
            label={`Unit cost${effectiveUom ? ` per ${effectiveUom}` : ''}`}
            hint="Stored against the lot, converted to the item's base unit"
            tooltip="What this delivery cost per unit. Cost lives on the lot rather than the item, so a cheaper delivery next month does not reprice the stock you already hold. It is what consumption and production cost are calculated from."
          >
            <input
              className={inputClass}
              value={unitCost}
              onChange={(event) => setUnitCost(event.target.value)}
              placeholder="412.00"
              inputMode="decimal"
              required
            />
          </Field>

          <Field label="Supplier" hint="Optional">
            <select
              className={inputClass}
              value={supplierId}
              onChange={(event) => setSupplierId(event.target.value)}
            >
              <option value="">Not recorded</option>
              {suppliers.data?.items.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.code} — {supplier.name}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="Expiry date"
            hint="Blank uses the item's shelf life, if it has one"
            tooltip="When this lot stops being good. Take it from the delivery note where there is one — a supplier's date beats a calculated one. Expiry is shown and reported, but it never blocks a draw on its own."
          >
            <input
              className={inputClass}
              type="date"
              value={expiryDate}
              onChange={(event) => setExpiryDate(event.target.value)}
            />
          </Field>

          <div className="sm:col-span-2 lg:col-span-3">
            <Field label="Note" hint="Optional">
              <input
                className={inputClass}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Purchase order, delivery note reference…"
              />
            </Field>
          </div>

          <div className="sm:col-span-2 lg:col-span-3">
            {error ? (
              <ErrorNote>
                {error instanceof ApiError
                  ? error.message
                  : 'Could not record the receipt. Is the API running?'}
              </ErrorNote>
            ) : null}

            <div className="mt-3 flex gap-2">
              <Button type="submit" disabled={receive.isPending}>
                {receive.isPending ? 'Receiving…' : 'Receive stock'}
              </Button>
              <Button tone="ghost" onClick={onDone}>
                Cancel
              </Button>
            </div>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}
